import {
  computed,
  effect,
  inject,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';

import { FetchResult } from '@apollo/client/core';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import {
  AddAssetGQL,
  Asset_Gestore_Insert_Input,
  Asset_Insert_Input,
  Asset_On_Conflict,
  Asset_Order_By,
  Cambia_Stato_Prosumer_Args,
  CambiaStatoProsumerGQL,
  CancellaCondivisioneAssetGQL,
  Cer_Bool_Exp,
  Cer_Cabina_Bool_Exp,
  ClearAssetNotificationGQL,
  CondividiAssetGQL,
  Condivisione_Condivisione,
  Condivisione_Condivisione_Insert_Input,
  DeleteAssetGQL,
  DeletePnrrDocumentiGQL,
  DeleteSottoscrizioneDocumentiGQL,
  Energy_Village__Azioni_Prosumer_Sottoscrizione_Enum,
  Energy_Village__Stato_Sottoscrizione_Enum,
  Exact,
  GetAssetDocument,
  GetAssetGQL,
  GetAssetInCabinaIdsGQL,
  GetAssetsCountDocument,
  GetAssetsCountSubscription,
  GetAssetsDocument,
  GetAssetsSubscription,
  GetAssetSubscription,
  GetCerDisponibiliProsumerDocument,
  GetCerDisponibiliProsumerSubscription,
  GetConfigurazioneGQL,
  GetGeomCabinaGQL,
  GetRipartizioniDocument,
  GetRipartizioniSubscription,
  GetRipartizioniTotaliDocument,
  GetRipartizioniTotaliSubscription,
  GetSottoscrizioneProsumerDocument,
  GetSottoscrizioneProsumerSubscription,
  GetSottoscrizioniProsumerDocument,
  GetSottoscrizioniProsumerSubscription,
  InputMaybe,
  InsertOrUpdateAssetsGQL,
  InsertOrUpdateSottoscrizioneGQL,
  InsertSottoscrizioneGQL,
  Profilo_Insert_Input,
  Profilo_On_Conflict,
  Scalars,
  Sottoscrizione_Bool_Exp,
  Sottoscrizione_Insert_Input,
  Sottoscrizione_On_Conflict,
  _Tipologia_Profilo_Enum,
  GetConfigurazioneQuery,
} from '../../../../_core/_graphql/generated';
import { ApolloRoleService } from '../../../../_core/_services/apollo-role.service';
import { LoadingService } from '../../../../_core/_services/loading.service';
import { StorageService } from '../../../../_core/_services/storage.service';
import { LoghiStore } from '../../../../_core/_signal-store/_loghi/loghi.store';
import { UserStore } from '../../_store/user.store';
import { initialProsumerState } from './prosumer.state';
import { of } from 'rxjs';
import { getTag } from '../../../../_core/_utils/get-tag';
import { DeepPartial } from '@apollo/client/utilities';
import { whiteLabel } from '../../../../_core/_utils/white-label';

function buildAssetsArgs(search: string) {
  const s = search.trim();
  return { p_search: s.length > 0 ? s : null };
}

export const ProsumerStore = signalStore(
  { providedIn: 'root' },
  withState(initialProsumerState),
  withComputed((store) => ({
    selected_asset_autoconsumo: computed(() => {
      const asset = store.selected_asset?.();
      return {
        type: asset?.tipologia_asset?.id.replace(/-/g, ''),
        autoconsumo: asset?.autoconsumo,
      };
    }),
  })),
  withMethods((store, injector = inject(Injector)) => {
    const subs = new Map<string, { unsubscribe: () => void }>();

    const setSub = (key: string, sub: { unsubscribe: () => void }) => {
      // se esiste, chiudi quella precedente
      subs.get(key)?.unsubscribe?.();
      subs.set(key, sub);
    };

    const clearSub = (key: string) => {
      subs.get(key)?.unsubscribe?.();
      subs.delete(key);
    };

    const clearAllSubs = (prefix?: string) => {
      for (const [k, s] of subs.entries()) {
        if (!prefix || k.startsWith(prefix)) {
          s?.unsubscribe?.();
          subs.delete(k);
        }
      }
    };

    return {
      init: () =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('user');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const pageSize = store.assets_page_size();
          const search = store.assets_search();
          const args = buildAssetsArgs(search);
          const order_by = store.assets_order_by();

          patchState(store, { assets_page: 0, assets_loading: true });

          clearSub('assets');
          const sub = _getter
            .subscribe<GetAssetsSubscription>({
              query: GetAssetsDocument,
              variables: { args, limit: pageSize, offset: 0, order_by },
            })
            .subscribe({
              next: (res: FetchResult<GetAssetsSubscription>) => {
                res?.data?.search_asset.forEach((asset) => {
                  const sottoscrizione = (
                    asset?.produzioni?.[0] ??
                    asset?.consumi?.[0] ??
                    undefined
                  )?.sottoscrizioniAttive?.[0];

                  if (sottoscrizione) {
                    _loghi.getLogo(sottoscrizione.cer_cabina?.cer?.logo?.id);
                    _loghi.getLogo(
                      sottoscrizione.cer_cabina?.cer?.cer_padre?.logo?.id,
                    );
                  }
                });
                patchState(store, {
                  assets_loading: false,
                  assets_data: res.data?.search_asset,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  assets_loading: false,
                  assets_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('assets', sub);

          clearSub('assetsCount');
          const countSub = _getter
            .subscribe<GetAssetsCountSubscription>({
              query: GetAssetsCountDocument,
              variables: { args },
            })
            .subscribe({
              next: (res) => {
                patchState(store, {
                  assets_total:
                    res.data?.search_asset_aggregate?.aggregate?.count ?? 0,
                });
              },
              error: () => {},
            });
          setSub('assetsCount', countSub);

          return sub;
        }),

      changePage: (page: number, requestedPageSize?: number) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('user');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const pageSize = requestedPageSize ?? store.assets_page_size();
          const offset = page * pageSize;
          const args = buildAssetsArgs(store.assets_search());
          const order_by = store.assets_order_by();

          patchState(store, {
            assets_page: page,
            assets_page_size: pageSize,
            assets_loading: true,
          });

          clearSub('assets');
          const sub = _getter
            .subscribe<GetAssetsSubscription>({
              query: GetAssetsDocument,
              variables: { args, limit: pageSize, offset, order_by },
            })
            .subscribe({
              next: (res: FetchResult<GetAssetsSubscription>) => {
                res?.data?.search_asset.forEach((asset) => {
                  const sottoscrizione = (
                    asset?.produzioni?.[0] ??
                    asset?.consumi?.[0] ??
                    undefined
                  )?.sottoscrizioniAttive?.[0];

                  if (sottoscrizione) {
                    _loghi.getLogo(sottoscrizione.cer_cabina?.cer?.logo?.id);
                    _loghi.getLogo(
                      sottoscrizione.cer_cabina?.cer?.cer_padre?.logo?.id,
                    );
                  }
                });
                patchState(store, {
                  assets_loading: false,
                  assets_data: res.data?.search_asset,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  assets_loading: false,
                  assets_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('assets', sub);
          return sub;
        }),

      applySearch: (search: string) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('user');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const pageSize = store.assets_page_size();
          const args = buildAssetsArgs(search);
          const order_by = store.assets_order_by();

          patchState(store, {
            assets_search: search,
            assets_page: 0,
            assets_loading: true,
          });

          clearSub('assets');
          const sub = _getter
            .subscribe<GetAssetsSubscription>({
              query: GetAssetsDocument,
              variables: { args, limit: pageSize, offset: 0, order_by },
            })
            .subscribe({
              next: (res: FetchResult<GetAssetsSubscription>) => {
                res?.data?.search_asset.forEach((asset) => {
                  const sottoscrizione = (
                    asset?.produzioni?.[0] ??
                    asset?.consumi?.[0] ??
                    undefined
                  )?.sottoscrizioniAttive?.[0];

                  if (sottoscrizione) {
                    _loghi.getLogo(sottoscrizione.cer_cabina?.cer?.logo?.id);
                    _loghi.getLogo(
                      sottoscrizione.cer_cabina?.cer?.cer_padre?.logo?.id,
                    );
                  }
                });
                patchState(store, {
                  assets_loading: false,
                  assets_data: res.data?.search_asset,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  assets_loading: false,
                  assets_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('assets', sub);

          clearSub('assetsCount');
          const countSub = _getter
            .subscribe<GetAssetsCountSubscription>({
              query: GetAssetsCountDocument,
              variables: { args },
            })
            .subscribe({
              next: (res) => {
                patchState(store, {
                  assets_total:
                    res.data?.search_asset_aggregate?.aggregate?.count ?? 0,
                });
              },
              error: () => {},
            });
          setSub('assetsCount', countSub);

          return sub;
        }),

      changeSort: (order_by: Asset_Order_By[]) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('user');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const pageSize = store.assets_page_size();
          const args = buildAssetsArgs(store.assets_search());

          patchState(store, {
            assets_order_by: order_by,
            assets_page: 0,
            assets_loading: true,
          });

          clearSub('assets');
          const sub = _getter
            .subscribe<GetAssetsSubscription>({
              query: GetAssetsDocument,
              variables: { args, limit: pageSize, offset: 0, order_by },
            })
            .subscribe({
              next: (res: FetchResult<GetAssetsSubscription>) => {
                res?.data?.search_asset.forEach((asset) => {
                  const sottoscrizione = (
                    asset?.produzioni?.[0] ??
                    asset?.consumi?.[0] ??
                    undefined
                  )?.sottoscrizioniAttive?.[0];

                  if (sottoscrizione) {
                    _loghi.getLogo(sottoscrizione.cer_cabina?.cer?.logo?.id);
                    _loghi.getLogo(
                      sottoscrizione.cer_cabina?.cer?.cer_padre?.logo?.id,
                    );
                  }
                });
                patchState(store, {
                  assets_loading: false,
                  assets_data: res.data?.search_asset,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  assets_loading: false,
                  assets_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('assets', sub);
          return sub;
        }),

      condividiAsset: (
        data: Condivisione_Condivisione_Insert_Input,
        tipo_profilo: _Tipologia_Profilo_Enum,
        trasferimento: boolean,
      ) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(CondividiAssetGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _userStore = inject(UserStore);
          const _loading = inject(LoadingService);
          _loading.start();
          return _mutation
            .mutate(
              {
                object: {
                  ...data,
                  tipo_profilo,
                  proprietario_id: _userStore.user_data?.()?.sub,
                },
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (e) => {
                  _toast.add({
                    key: 'global-toast',
                    severity: 'success',
                    summary: _translate.instant(
                      'shareAsset.toast.success.summary',
                      { trasferimento },
                    ),
                    detail: _translate.instant(
                      'shareAsset.toast.success.detail',
                      { trasferimento },
                    ),
                  });
                  _loading.stop();
                  return e.data?.insert_condivisione_condivisione_one;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },

      loadSelectedRipartizioni: (
        cer_id?: string,
        sottoscrizione_id?: string,
      ) => {
        return runInInjectionContext(injector, () => {
          if (cer_id) {
            const _apollo = inject(ApolloRoleService);
            const _getter = _apollo.getClient('user');
            const _toast = inject(MessageService);
            const _translate = inject(TranslateService);

            patchState(store, { selected_ripartizioni_loading: true });

            clearSub('selectedRipartizioni');

            const sub = _getter
              .subscribe<GetRipartizioniSubscription>({
                query: GetRipartizioniDocument,
                variables: {
                  cer_id,
                  sottoscrizione_id,
                },
              })
              .subscribe({
                next: (res) => {
                  patchState(store, {
                    selected_ripartizioni_loading: false,
                    selected_ripartizioni_data: res.data?.incentivi_calcolati,
                  });
                },
                error: (error) => {
                  patchState(store, {
                    selected_ripartizioni_loading: false,
                    selected_ripartizioni_data: undefined,
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              });

            setSub('selectedRipartizioni', sub);
            return sub;
          } else {
            return of(null);
          }
        });
      },
      clearSelectedRipartizioni: () => {
        runInInjectionContext(injector, async () => {
          clearSub('selectedRipartizioni');

          patchState(store, {
            selected_ripartizioni_loading: false,
            selected_ripartizioni_data: undefined,
          });
        });
      },

      cancellaCondivisioneAsset: (id: any, trasferimento: boolean) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(CancellaCondivisioneAssetGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _userStore = inject(UserStore);
          const _loading = inject(LoadingService);
          _loading.start();
          return _mutation
            .mutate(
              {
                id,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (e) => {
                  _toast.add({
                    key: 'global-toast',
                    severity: 'success',
                    summary: _translate.instant(
                      'shareAsset.toast.remove.summary',
                      {
                        trasferimento,
                      },
                    ),
                    detail: _translate.instant(
                      'shareAsset.toast.remove.detail',
                      { trasferimento },
                    ),
                  });
                  _loading.stop();
                  return e.data;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },

      saveAsset: (data: Asset_Gestore_Insert_Input) => {
        return runInInjectionContext(injector, () => {
          const _addAsset = inject(AddAssetGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _userStore = inject(UserStore);
          const _loading = inject(LoadingService);
          _loading.start();
          return _addAsset
            .mutate(
              {
                objects: { ...data, auth_id: _userStore.user_data?.()?.sub },
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (e) => {
                  _toast.add({
                    key: 'global-toast',
                    severity: 'success',
                    summary: _translate.instant(
                      'app.private.profiling.toast.asset.success.summary',
                    ),
                    detail: _translate.instant(
                      'app.private.profiling.toast.asset.success.detail',
                      { type: 'create' },
                    ),
                  });
                  _loading.stop();
                  return e.data?.insert_asset_gestore?.returning[0];
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
      insertOrUpdateAsset: (
        objects: Asset_Insert_Input,
        on_conflict: Asset_On_Conflict,
      ) => {
        return runInInjectionContext(injector, () => {
          const _insertOrUpdate = inject(InsertOrUpdateAssetsGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);
          return _insertOrUpdate
            .mutate(
              {
                objects,
                on_conflict,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (e) => {
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'success',
                    summary: _translate.instant(
                      'app.private.profiling.toast.asset.success.summary',
                    ),
                    detail: _translate.instant(
                      'app.private.profiling.toast.asset.success.detail',
                      { type: 'edit' },
                    ),
                  });
                  if (
                    e.data?.insert_asset?.returning[0].id ==
                    store.selected_asset?.()?.id
                  ) {
                    patchState(store, {
                      selected_asset: e.data?.insert_asset?.returning[0],
                    });
                  }
                  return e.data?.insert_asset;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
      patchSelectedAsset: (asset: any, loading: boolean = false) => {
        console.log('asset prosumer store', asset);
        patchState(store, {
          selected_asset: asset,
          selected_asset_loading: loading,
        });
      },
      loadSelectedAsset: (id: string) =>
        runInInjectionContext(injector, () => {
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _clear = inject(ClearAssetNotificationGQL);

          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('user');

          patchState(store, { selected_asset_loading: true });

          clearSub('asset');

          const sub = _getter
            .subscribe<GetAssetSubscription>({
              query: GetAssetDocument,
              variables: { id },
            })

            .subscribe({
              next: (res) => {
                _clear
                  .mutate(
                    { id: res.data?.asset_by_pk?.id },
                    {
                      context: {
                        headers: { 'x-hasura-role': 'user' },
                      },
                    },
                  )
                  .subscribe(() => {
                    patchState(store, {
                      selected_asset: res.data?.asset_by_pk,
                      selected_asset_loading: false,
                    });
                  });
              },
              error: (error) => {
                patchState(store, {
                  selected_asset: undefined,
                  selected_asset_loading: false,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('asset', sub);
        }),
      deleteAsset: (id: string) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(DeleteAssetGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);
          _loading.start();
          return _mutation
            .mutate(
              {
                where: {
                  id: {
                    _eq: id,
                  },
                },
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'success',
                    summary: _translate.instant(
                      'app.private.profiling.toast.asset.success.summary',
                    ),
                    detail: _translate.instant(
                      'app.private.profiling.toast.asset.success.detail',
                      { type: 'delete' },
                    ),
                  });
                  return res;
                },
                error: (error: any) => {
                  const detail = error.errors.some(
                    (el: any) => el?.extensions?.code == 'constraint-violation',
                  )
                    ? _translate.instant('toast.error.fk')
                    : _translate.instant('toast.error.detail', { error });
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail,
                  });
                },
              }),
            );
        });
      },
      clearSelectedAsset: () => {
        clearSub('asset');
        clearSub('publicAssets');
        clearSub('sottoscrizioni');
        clearSub('cerDisponibili');
        clearSub('selectedSottoscrizione');

        patchState(store, {
          selected_asset_geometria: undefined,
          selected_asset_geometria_loading: false,
          selected_asset: undefined,
          selected_asset_loading: false,
          selected_asset_energy_village_assets: undefined,
          selected_asset_energy_village_assets_loading: false,

          sottoscrizioni_loading: false,
          sottoscrizioni_data: undefined,
          cer_disponibili_loading: false,
          cer_disponibili_data: undefined,

          selected_sottoscrizione_loading: false,
          selected_sottoscrizione: undefined,

          selected_configurazione: undefined,
          selected_configurazione_loading: false,
          selected_configurazione_logo: undefined,

          cer_disponibili_filter: false,
        });
      },
      initSottoscrizioni: (id: number, asset_id: string) => {
        return runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('user');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);
          patchState(store, { sottoscrizioni_loading: true });

          // chiudi eventuale sub precedente per questa chiave logica
          clearSub('sottoscrizioni');

          const sub = _getter
            .subscribe<
              GetSottoscrizioniProsumerSubscription,
              Exact<{
                where?: InputMaybe<Sottoscrizione_Bool_Exp>;
              }>
            >({
              query: GetSottoscrizioniProsumerDocument,
              variables: {
                where: {
                  asset_consumo: {
                    asset_id: {
                      _eq: asset_id,
                    },
                  },
                },
              },
            })
            .subscribe({
              next: (res) => {
                const sottoscrizioni:
                  | GetSottoscrizioniProsumerSubscription['sottoscrizione']
                  | undefined = res.data?.sottoscrizione;

                if ((sottoscrizioni?.length ?? 0) == 0) {
                  ////RELOAD
                  patchState(store, {
                    sottoscrizioni_loading: false,
                    sottoscrizioni_data: undefined,
                  });
                } else {
                  sottoscrizioni?.map((s) => {
                    _loghi.getLogo(s.cer_cabina?.cer?.logo?.id);
                    _loghi.getLogo(s.cer_cabina?.cer?.cer_padre?.logo?.id);
                  });
                  patchState(store, {
                    sottoscrizioni_loading: false,
                    sottoscrizioni_data: sottoscrizioni,
                  });
                }
              },
              error: (error) => {
                patchState(store, {
                  sottoscrizioni_loading: false,
                  sottoscrizioni_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          // registra la nuova sub
          setSub('sottoscrizioni', sub);
          return sub; // se vuoi poterci interagire a valle
        });
      },
      initCerDisponibili: (id: number, asset_id: string, filter?: any) => {
        return runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('user');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);
          patchState(store, {
            cer_disponibili_loading: true,
          });

          // chiudi eventuale sub precedente per questa chiave logica
          clearSub('cerDisponibili');

          let or = [];
          let or_stato_cer = [];
          let bypass = false;

          if (filter?.stato_cer) {
            // or = Object.keys(filter?.stato).reduce((acc: any, k) => {
            //   const a = acc;
            //   if (k == 'da_inviare' && filter?.stato?.[k] == true) bypass = true;
            //   if (
            //     filter?.stato?.[k] &&
            //     filter?.stato?.[k] != false &&
            //     k !== 'da_inviare'
            //   ) {
            //     a.push({
            //       stato: { _eq: k },
            //     });
            //   }
            //   return a;
            // }, []);
            or_stato_cer = Object.keys(filter?.stato_cer).reduce(
              (acc: any, k) => {
                const a = acc;
                if (filter?.stato_cer?.[k] && filter?.stato_cer?.[k] != false) {
                  a.push({
                    stato_cer: { _eq: k },
                  });
                }
                return a;
              },
              [],
            );
            patchState(store, {
              cer_disponibili_filter:
                Object.keys(filter?.stato_cer).length === 0 ? false : true,
            });
          }

          const referral = localStorage.getItem('referral');

          const sub = _getter
            .subscribe<GetCerDisponibiliProsumerSubscription>({
              query: GetCerDisponibiliProsumerDocument,
              variables: {
                where: {
                  ...whiteLabel(),
                  cer_cabina: {
                    ...(referral
                      ? {
                          cer: {
                            _or: [
                              { referral: { _eq: referral } },
                              {
                                cer_padre: {
                                  referral: { _eq: referral },
                                },
                              },
                            ],
                          },
                        }
                      : {}),
                    cabina: {
                      id: {
                        _eq: id,
                      },
                    },
                  },
                  root_id: {
                    _is_null: false,
                  },
                  _and: [
                    ...(or_stato_cer.length > 0 ? [{ _or: or_stato_cer }] : []),
                  ],
                },
                where_cer_cabina: {
                  cabina_id: {
                    _eq: id,
                  },
                },
                where_sottoscrizioni: {
                  asset_consumo: {
                    asset_id: {
                      _eq: asset_id,
                    },
                  },
                },
              },
            })
            .subscribe({
              next: (res) => {
                const cers:
                  | GetCerDisponibiliProsumerSubscription['cer']
                  | undefined = res.data?.cer.filter((cer) => {
                  return (
                    cer.cer_cabina[0].sottoscrizioni.length === 0 ||
                    cer.cer_cabina[0].sottoscrizioni.every(
                      (e) =>
                        e.stato ==
                        Energy_Village__Stato_Sottoscrizione_Enum.Cancellata,
                    )
                  );
                });
                if (
                  (cers?.length ?? 0) == 0 &&
                  filter?.referral !== undefined
                ) {
                  ////RELOAD
                  patchState(store, {
                    cer_disponibili_loading: false,
                    cer_disponibili_data: undefined,
                  });
                } else {
                  cers?.map((c) => {
                    _loghi.getLogo(c.logo?.id);
                    _loghi.getLogo(c.cer_padre?.logo?.id);
                  });
                  patchState(store, {
                    cer_disponibili_loading: false,
                    cer_disponibili_data: cers,
                  });
                }
              },
              error: (error) => {
                patchState(store, {
                  cer_disponibili_loading: false,
                  cer_disponibili_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          // registra la nuova sub
          setSub('cerDisponibili', sub);
          return sub; // se vuoi poterci interagire a valle
        });
      },
      cambiaStato: (args: InputMaybe<Cambia_Stato_Prosumer_Args>) => {
        return runInInjectionContext(injector, () => {
          if (
            args?.action !=
            Energy_Village__Azioni_Prosumer_Sottoscrizione_Enum.SalvaPerDopo
          ) {
            const _mutation = inject(CambiaStatoProsumerGQL);
            const _toast = inject(MessageService);
            const _translate = inject(TranslateService);
            patchState(store, { selected_sottoscrizione_loading: true });

            return _mutation
              .mutate(
                {
                  args,
                },
                {
                  context: {
                    headers: { 'x-hasura-role': 'user' },
                  },
                },
              )
              .pipe(
                tapResponse({
                  next: (res) => {
                    return res;
                  },
                  error: (error) => {
                    patchState(store, {
                      failure: error,
                      selected_sottoscrizione_loading: false,
                    });
                    _toast.add({
                      key: 'global-toast',
                      severity: 'error',
                      summary: _translate.instant('toast.error.title'),
                      detail: _translate.instant('toast.error.detail', {
                        error,
                      }),
                    });
                  },
                }),
              );
          } else {
            return of({});
          }
        });
      },
      insertSottoscrizione: (data: Sottoscrizione_Insert_Input) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(InsertSottoscrizioneGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _prosumerStore = inject(ProsumerStore);

          return _mutation
            .mutate(
              {
                object: data,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  _prosumerStore.loadSelectedSottoscrizione(
                    res.data?.insert_sottoscrizione_one?.id,
                    true,
                  );

                  return res;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
      insertOrUpdateSottoscrizione: (
        objects: Sottoscrizione_Insert_Input,
        on_conflict: Sottoscrizione_On_Conflict,
      ) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(InsertOrUpdateSottoscrizioneGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _prosumerStore = inject(ProsumerStore);

          const sanitizeIndirizzoData = (value: any): any => {
            if (!value || typeof value !== 'object') return value;
            const next: any = Array.isArray(value) ? [] : {};
            for (const [key, raw] of Object.entries(value)) {
              if (key === '__typename') continue;
              if (
                key === 'citta' ||
                key === 'comune' ||
                key === 'city' ||
                key === 'locality' ||
                key === 'geom'
              ) {
                continue;
              }
              if (Array.isArray(raw)) {
                next[key] = raw.map((entry) => sanitizeIndirizzoData(entry));
              } else if (raw && typeof raw === 'object') {
                next[key] = sanitizeIndirizzoData(raw);
              } else {
                next[key] = raw;
              }
            }
            return next;
          };

          const sanitizeMutationObject = (value: any, parentKey?: string): any => {
            if (Array.isArray(value)) {
              return value.map((entry) => sanitizeMutationObject(entry, parentKey));
            }
            if (value instanceof Date) return value;
            if (!value || typeof value !== 'object') return value;
            const next: any = {};
            for (const [key, raw] of Object.entries(value)) {
              if (key === '__typename') continue;
              if (parentKey === 'indirizzo' && key === 'data') {
                next[key] = sanitizeIndirizzoData(raw);
              } else {
                next[key] = sanitizeMutationObject(raw, key);
              }
            }
            return next;
          };

          const sanitizedObjects = sanitizeMutationObject(
            objects,
          ) as Sottoscrizione_Insert_Input;

          return _mutation
            .mutate(
              {
                objects: sanitizedObjects,
                on_conflict,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  return res;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
      deleteDocumentiSottoscrizione: (id: string[]) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(DeleteSottoscrizioneDocumentiGQL);
          return _mutation
            .mutate(
              {
                where: {
                  id: {
                    _in: id,
                  },
                },
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  return res;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                },
              }),
            );
        });
      },
      deleteDocumentiPnrrSottoscrizione: (id: string[]) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(DeletePnrrDocumentiGQL);
          return _mutation
            .mutate(
              {
                where: {
                  id: {
                    _in: id,
                  },
                },
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'user' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  return res;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                },
              }),
            );
        });
      },
      patchSelectedSottoscrizione: (
        sottoscrizione: GetSottoscrizioneProsumerSubscription['sottoscrizione_by_pk'],
      ) => {
        patchState(store, { selected_sottoscrizione: sottoscrizione });
      },
      loadSelectedSottoscrizione: (id?: number, loading: boolean = true) => {
        return runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('user');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          if (!id) {
            patchState(store, {
              selected_sottoscrizione: undefined,
              selected_sottoscrizione_loading: false,
            });

            return;
          }
          if (loading)
            patchState(store, { selected_sottoscrizione_loading: true });

          clearSub('selectedSottoscrizione');

          const sub = _getter
            .subscribe<
              GetSottoscrizioneProsumerSubscription,
              Exact<{ id: Scalars['uuid']['input'] }>
            >({
              query: GetSottoscrizioneProsumerDocument,
              variables: {
                id,
              },
            })
            .subscribe({
              next: (res) => {
                if (loading) {
                  patchState(store, {
                    selected_sottoscrizione_loading: false,
                    selected_sottoscrizione: res.data?.sottoscrizione_by_pk,
                  });
                } else {
                  patchState(store, {
                    selected_sottoscrizione: res.data?.sottoscrizione_by_pk,
                  });
                }
              },
              error: (error) => {
                patchState(store, {
                  selected_sottoscrizione_loading: false,
                  selected_sottoscrizione: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('selectedSottoscrizione', sub);
          return sub;
        });
      },
      clearSelectedSottoscrizione: () => {
        runInInjectionContext(injector, async () => {
          clearSub('selectedSottoscrizione');

          patchState(store, {
            selected_sottoscrizione_loading: false,
            selected_sottoscrizione: undefined,
          });
        });
      },

      patchSelectedConfigurazione: (
        cer: DeepPartial<GetConfigurazioneQuery['cer_by_pk']>,
      ) => {
        patchState(store, { selected_configurazione: cer });
      },
      loadSelectedConfigurazione: (id: string, cabina_id?: number) => {
        return runInInjectionContext(injector, () => {
          const _getter = inject(GetConfigurazioneGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _storage = inject(StorageService);
          const _loghi = inject(LoghiStore);

          patchState(store, { selected_configurazione_loading: true });

          return _getter
            .fetch(
              {
                id,
                ...(cabina_id
                  ? {
                      cabina_id: {
                        _eq: cabina_id,
                      },
                    }
                  : {}),
              },
              { context: { headers: { 'x-hasura-role': 'user' } } },
            )
            .subscribe({
              next: (res) => {
                _loghi.getLogo(res.data?.cer_by_pk?.cer_padre?.logo?.id);

                patchState(store, {
                  selected_configurazione_loading: false,
                  selected_configurazione: res.data?.cer_by_pk,
                });
              },
              error: (error) => {
                patchState(store, {
                  selected_configurazione_loading: false,
                  selected_configurazione: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
        });
      },
      clearSelectedConfigurazione: () => {
        runInInjectionContext(injector, async () => {
          patchState(store, {
            selected_configurazione_loading: false,
            selected_configurazione: undefined,
          });
        });
      },

      clear: () => {
        clearAllSubs();
        patchState(store, initialProsumerState);
      },
      effectOnInit: () => {
        return runInInjectionContext(injector, () => {
          const _getter = inject(GetGeomCabinaGQL);
          const _getterAssets = inject(GetAssetInCabinaIdsGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _apolloIncentivi = inject(ApolloRoleService);

          effect(() => {
            const assets = store.assets_data?.() ?? [];
            const ids = assets
              .map(
                (a) =>
                  (a?.produzioni?.[0] ?? a?.consumi?.[0])
                    ?.sottoscrizioniAttive?.[0]?.id,
              )
              .filter(Boolean) as string[];

            clearSub('incentiviTotali');
            patchState(store, { assets_incentivi_totali: {} });

            if (ids.length === 0) return;

            const sub = _apolloIncentivi
              .getClient('user')
              .subscribe<GetRipartizioniTotaliSubscription>({
                query: GetRipartizioniTotaliDocument,
                variables: { sottoscrizione_ids: ids },
              })
              .subscribe({
                next: (res) => {
                  // Inizializza tutti gli id a 0: chi non ha record resta 0 → "---"
                  const totali: Record<string, number> = Object.fromEntries(
                    ids.map((id) => [id, 0]),
                  );
                  for (const item of res.data?.incentivi_calcolati ?? []) {
                    const id = item.sottoscrizione_id as string;
                    const t = item.aggregati?.tabella ?? {};
                    const sum = (['cona', 'prod'] as const).reduce(
                      (acc, k) =>
                        acc +
                        (t[k]?.base ?? 0) +
                        (t[k]?.ecc ?? 0) +
                        (t[k]?.val ?? 0),
                      0,
                    );
                    totali[id] = (totali[id] ?? 0) + sum;
                  }
                  patchState(store, { assets_incentivi_totali: totali });
                },
                error: () => {},
              });
            setSub('incentiviTotali', sub);
          });

          effect(() => {
            const cabina_id = store.selected_asset?.()?.cabina?.id;
            if (cabina_id) {
              patchState(store, { selected_asset_geometria_loading: true });
              _getter
                .fetch(
                  { _in: [cabina_id] },
                  {
                    context: {
                      headers: { 'x-hasura-role': 'user' },
                    },
                    fetchPolicy: 'cache-first',
                  },
                )
                .subscribe({
                  next: (res) => {
                    patchState(store, {
                      selected_asset_geometria:
                        res.data?.energy_village_area?.[0],
                      selected_asset_geometria_loading: false,
                    });
                  },
                  error: (error) => {
                    patchState(store, {
                      selected_asset_geometria: undefined,
                      selected_asset_geometria_loading: false,
                      failure: error,
                    });
                    _toast.add({
                      key: 'global-toast',
                      severity: 'error',
                      summary: _translate.instant('toast.error.title'),
                      detail: _translate.instant('toast.error.detail', {
                        error,
                      }),
                    });
                  },
                });

              patchState(store, {
                selected_asset_energy_village_assets_loading: true,
              });

              clearSub('publicAssets');

              const sub = _getterAssets
                .subscribe({ cabina_ids: [cabina_id] })
                .subscribe({
                  next: (res) => {
                    patchState(store, {
                      selected_asset_energy_village_assets: res.data?.asset,
                      selected_asset_energy_village_assets_loading: false,
                    });
                  },
                  error: (error) => {
                    patchState(store, {
                      selected_asset_energy_village_assets: undefined,
                      selected_asset_energy_village_assets_loading: false,
                      failure: error,
                    });
                    _toast.add({
                      key: 'global-toast',
                      severity: 'error',
                      summary: _translate.instant('toast.error.title'),
                      detail: _translate.instant('toast.error.detail', {
                        error,
                      }),
                    });
                  },
                });

              setSub('publicAssets', sub);
            }
          });
        });
      },
    };
  }),
  withHooks({
    onInit(store) {
      store.effectOnInit();
    },
  }),
);
